import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { FaFilter } from 'react-icons/fa';
import { MdClear, MdOutlineEventRepeat, MdOutlineCancel } from 'react-icons/md';
import { AiOutlineEye } from 'react-icons/ai';
import { BsSearch } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import Select from 'components/inputs/Select';
import OffCanvas from 'components/inputs/OffCanvas';
import ReactPagination from 'components/general/ReactPagination';
import Footer from 'components/general/Footer';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import CheckBox from 'components/inputs/CheckBox';
import { API } from 'api';
import { FETCH_FLEET_RESERVATIONS } from 'actions/fleetReservation';
import FleetReservationCreate from './FleetReservationCreate';
import {
  StatusPill,
  canManageFleetReservations,
  canRescheduleRow,
  canCancelRow,
  STATUS_FILTER_OPTIONS,
  connectorLabel,
  formatDate,
  formatWindow,
  loadStations,
  toStationOptions,
  asArray,
  unwrap,
} from './fleetReservationUtils';

const COLUMNS = ['Reservation Code', 'Fleet Name', 'Station', 'Charger ID', 'Connector', 'Date', 'Time Window', 'Status', 'Actions'];
const initialFilters = { search: '', fleetOwnerId: '', stationId: '', status: '', dateFrom: '', dateTo: '', upcomingOnly: false };
const ADVANCED_KEYS = ['fleetOwnerId', 'stationId', 'status', 'upcomingOnly'];

const FleetReservationList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { navigateTo } = useContext(NavContext);
  const { results, isLoading, page, limit, total, totalPages } = useSelector((state) => state.fleetReservation);
  const rolePermissionData = useSelector((state) => state.manageRole.manageRoles);
  const profileData = useSelector((state) => state.profile.userProfile);
  const canManage = canManageFleetReservations(profileData, rolePermissionData);
  const [filters, setFilters] = useState(initialFilters);
  const [showAdvanceFilter, setShowAdvanceFilter] = useState(false);
  const [advanceDraft, setAdvanceDraft] = useState(_.pick(initialFilters, ADVANCED_KEYS));
  const [searchText, setSearchText] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [fleetOption, setFleetOption] = useState(null);
  const [draftFleetOption, setDraftFleetOption] = useState(null);
  const [stations, setStations] = useState([]);
  const [isStationsLoading, setIsStationsLoading] = useState(false);
  const activeAdvanceCount = ADVANCED_KEYS.filter((key) => filters[key]).length;

  const getList = useCallback(
    (params = {}) => {
      const query = _.omitBy({ page: 1, limit, ...filters, ...params }, (value) => value === '' || _.isNil(value) || value === false);
      dispatch({ type: FETCH_FLEET_RESERVATIONS.REQUEST, payload: query });
    },
    [filters, limit]
  );

  useEffect(() => {
    getList();
  }, [filters]);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const applySearch = useMemo(() => _.debounce((value) => setFilter('search', value.trim()), 300), []);
  useEffect(() => () => applySearch.cancel(), []);

  const openRowAction = (row, action) =>
    navigateTo(`/fleet-reservations/${row.reservationCode}`, { state: { action, occurrenceDate: row.reservationDate } });

  const ensureStations = () => {
    if (stations.length || isStationsLoading) return;
    setIsStationsLoading(true);
    loadStations()
      .then(setStations)
      .catch(() => setStations([]))
      .finally(() => setIsStationsLoading(false));
  };

  const openAdvanceFilter = () => {
    ensureStations();
    setAdvanceDraft(_.pick(filters, ADVANCED_KEYS));
    setDraftFleetOption(fleetOption);
    setShowAdvanceFilter(true);
  };

  const applyAdvanceFilter = (values, option) => {
    setFleetOption(option);
    setFilters((prev) => ({ ...prev, ...values, fleetOwnerId: _.get(option, 'value', '') }));
    setShowAdvanceFilter(false);
  };

  const loadFleetFilterOptions = useMemo(
    () =>
      _.debounce((search, callback) => {
        API.getFleetReservationFleetOptions({ search, limit: 20 })
          .then((res) => callback(asArray(unwrap(_.get(res, 'data'))).map((owner) => ({ label: owner.label || owner.name, value: owner.ownerId }))))
          .catch(() => callback([]));
      }, 300),
    []
  );
  useEffect(() => () => loadFleetFilterOptions.cancel(), []);
  const stationOptions = useMemo(() => [{ label: 'All stations', value: '' }, ...toStationOptions(stations)], [stations]);
  const showSkeleton = isLoading && _.isEmpty(results);

  return (
    <LayoutContainer>
      <AdminHeader title={t('header.fleetReservation', 'Fleet Reservation')} />
      <SkeletonTheme baseColor="#1b2a38" highlightColor="#092235">
        <div className="page-content-wrapper scrollable fr-page">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div className="fr-subtitle">Reserved charging capacity by date. One row is one charger on one date.</div>
            {canManage && <Button onClick={() => setShowCreate(true)}>+ Create Reservation</Button>}
          </div>

          <Card>
            <div className="fr-toolbar">
              <div className="fr-toolbar__field fr-toolbar__field--search">
                <div className="fr-label">Search</div>
                <div className="fr-search">
                  <BsSearch className="fr-search__icon" />
                  <input
                    className="form-control fr-input"
                    placeholder="Code, fleet, phone, station, charger ID"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      applySearch(e.target.value);
                    }}
                  />
                </div>
              </div>
              <div className="fr-toolbar__field">
                <div className="fr-label">Date from</div>
                <input
                  type="date"
                  className="form-control fr-input"
                  value={filters.dateFrom}
                  onChange={(e) => setFilter('dateFrom', e.target.value)}
                />
              </div>
              <div className="fr-toolbar__field">
                <div className="fr-label">Date to</div>
                <input
                  type="date"
                  className="form-control fr-input"
                  value={filters.dateTo}
                  min={filters.dateFrom || undefined}
                  onChange={(e) => setFilter('dateTo', e.target.value)}
                />
              </div>
              <div className="fr-toolbar__actions">
                <Button className="report-search-button" onClick={openAdvanceFilter}>
                  <FaFilter className="me-2" />
                  {t('button.advancedFilter', 'Advanced Filter')}
                  {activeAdvanceCount > 0 && <span className="fr-count">{activeAdvanceCount}</span>}
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            <div className={`table-responsive ${isLoading && !showSkeleton ? 'fr-refreshing' : ''}`}>
              <table className="record-list-table">
                <thead>
                  <tr>
                    {COLUMNS.map((heading) => (
                      <th key={heading}>{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {showSkeleton ? (
                    _.times(5, (index) => (
                      <tr key={index}>
                        <td colSpan={COLUMNS.length}>
                          <Skeleton height={15} />
                        </td>
                      </tr>
                    ))
                  ) : _.isEmpty(results) ? (
                    <tr>
                      <td colSpan={COLUMNS.length} className="border-0">
                        <div className="empty-data-block">No reservations found</div>
                      </td>
                    </tr>
                  ) : (
                    results.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className="fr-link" onClick={() => navigateTo(`/fleet-reservations/${row.reservationCode}`)}>
                            {row.reservationCode}
                          </span>
                        </td>
                        <td>
                          {row.fleetName}
                          {row.memberName && <div className="fr-muted small">{row.memberName}</div>}
                        </td>
                        <td>{row.stationName}</td>
                        <td>{row.chargerOcppId || '—'}</td>
                        <td>{connectorLabel(row.connectorId, row.connectorName) || '—'}</td>
                        <td>{formatDate(row.reservationDate, 'YYYY-MM-DD')}</td>
                        <td>
                          {formatWindow(row.bookedStartTime, row.bookedEndTime)}
                          {row.blockStartTime && (
                            <div className="fr-blocked small">Blocked {formatWindow(row.blockStartTime, row.bookedEndTime)}</div>
                          )}
                        </td>
                        <td>
                          <StatusPill status={row.status} />
                        </td>
                        <td>
                          <div className="fr-row-actions">
                            <button type="button" title="View details" onClick={() => navigateTo(`/fleet-reservations/${row.reservationCode}`)}>
                              <AiOutlineEye size={18} />
                            </button>
                            {canManage && canRescheduleRow(row) && (
                              <button type="button" title="Reschedule" onClick={() => openRowAction(row, 'reschedule')}>
                                <MdOutlineEventRepeat size={18} />
                              </button>
                            )}
                            {canManage && canCancelRow(row) && (
                              <button type="button" title="Cancel" className="is-danger" onClick={() => openRowAction(row, 'cancel')}>
                                <MdOutlineCancel size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {!isLoading && total > 0 && (
              <ReactPagination
                currentPage={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                handlePageClick={(selected) => getList({ page: selected.selected + 1 })}
                marginPagesDisplayed={1}
              />
            )}
          </Card>
        </div>
      </SkeletonTheme>

      {showAdvanceFilter && (
        <OffCanvas show={showAdvanceFilter} onClose={() => setShowAdvanceFilter(false)}>
          <div className="mb-5">
            <Select
              label="Fleet"
              isAsync
              isClearable
              cacheOptions
              defaultOptions
              loadOptions={loadFleetFilterOptions}
              placeholder="Search fleet owner"
              value={draftFleetOption}
              onChange={setDraftFleetOption}
            />
            <Select
              label="Station"
              isLoading={isStationsLoading}
              placeholder={isStationsLoading ? 'Loading stations…' : 'All stations'}
              options={stationOptions}
              value={advanceDraft.stationId}
              onChange={(value) => setAdvanceDraft((prev) => ({ ...prev, stationId: value || '' }))}
              disableSort
            />
            <Select
              label="Status"
              options={STATUS_FILTER_OPTIONS}
              value={advanceDraft.status}
              onChange={(value) => setAdvanceDraft((prev) => ({ ...prev, status: value || '' }))}
              disableSort
            />
            <CheckBox
              id="fr-upcoming"
              name="upcomingOnly"
              checked={!!advanceDraft.upcomingOnly}
              onChange={(e) => setAdvanceDraft((prev) => ({ ...prev, upcomingOnly: e.target.checked }))}
            >
              Upcoming only
            </CheckBox>
          </div>
          <div className="canvas-btn__block">
            <Button className="apply--btn w-100" onClick={() => applyAdvanceFilter(advanceDraft, draftFleetOption)}>
              {t('button.applyFilters', 'Apply Filters')}
            </Button>
            <div className="reset--block" onClick={() => applyAdvanceFilter(_.pick(initialFilters, ADVANCED_KEYS), null)}>
              <MdClear size={24} className="reset--icon" />
              {t('button.clear', 'Clear')}
            </div>
          </div>
        </OffCanvas>
      )}
      {showCreate && (
        <FleetReservationCreate
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            getList();
          }}
        />
      )}
      <Footer />
    </LayoutContainer>
  );
};

export default FleetReservationList;
